import { httpClient } from '../api/http.client';
import { API_CONFIG, getAuthToken } from '../config/api.config';
import {
  ImportJob,
  UploadImportResponse,
  ImportStatusResponse,
  ImportHistoryResponse,
} from '@/domain/entities';

// URL для Import Service (можно вынести в конфигурацию)
const getImportServiceUrl = (): string => {
  // В проде это должен быть URL Import Service
  // Пока используем localhost для разработки
  if (typeof process !== 'undefined' && process.env && process.env.VITE_IMPORT_SERVICE_URL) {
    return process.env.VITE_IMPORT_SERVICE_URL;
  }
  // По умолчанию используем тот же URL что и Admin API
  // В проде нужно будет настроить правильный URL Import Service
  return API_CONFIG.baseUrl.replace(/\/$/, '');
};

export class ImportRepository {
  private importServiceUrl: string;

  constructor() {
    this.importServiceUrl = getImportServiceUrl();
  }

  async uploadFile(file: File): Promise<UploadImportResponse> {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);

    // Проверяем, используем ли мы Import Service напрямую или через Admin API
    // Если Admin API проксирует запросы, используем Admin API endpoint
    const isImportServiceDirect = this.importServiceUrl.includes(':8080') || 
                                   this.importServiceUrl !== API_CONFIG.baseUrl.replace(/\/$/, '');

    let url: string;
    if (isImportServiceDirect) {
      // Прямой запрос к Import Service (требует Service Token)
      // В этом случае нужно использовать Service Token, а не JWT
      // Для фронтенда лучше использовать прокси через Admin API
      url = `${this.importServiceUrl}/api/import/upload`;
    } else {
      // Прокси через Admin API (использует JWT токен)
      url = `${this.importServiceUrl}/api/admin/import/upload`;
    }

    const headers: HeadersInit = {
      ...(formData ? {} : { 'Content-Type': 'application/json' }),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 секунд для загрузки файла

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 204) {
        return {} as UploadImportResponse;
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error((data as { error?: string }).error || response.statusText);
      }

      return data as UploadImportResponse;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  async getJobStatus(jobId: string): Promise<ImportStatusResponse> {
    // Получаем статус через Admin API
    return httpClient.get<ImportStatusResponse>(`/api/admin/import/jobs/${jobId}`);
  }

  async getHistory(limit: number = 20, offset: number = 0): Promise<ImportHistoryResponse> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    return httpClient.get<ImportHistoryResponse>(
      `/api/admin/import/jobs?${params.toString()}`
    );
  }

  async getJob(jobId: string): Promise<ImportJob> {
    return httpClient.get<ImportJob>(`/api/admin/import/jobs/${jobId}`);
  }

  async deleteJob(jobId: string): Promise<void> {
    return httpClient.delete<void>(`/api/admin/import/jobs/${jobId}`);
  }
}

export const importRepository = new ImportRepository();

